import _ from "lodash";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Spin, message, Card, Flex, Tabs, TabsProps } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { ReactComponent as IconLocation } from "assets/images/location.svg";
import { ReactComponent as IconHouse } from "assets/images/house.svg";
import { ReactComponent as IconRuler } from "assets/images/ruler.svg";
import { ReactComponent as IconCalendar } from "assets/images/calendar-2.svg";
import { ReactComponent as IconStair } from "assets/images/stair.svg";
import { ReactComponent as IconEnergy } from "assets/images/energy.svg";
import { ReactComponent as IconTemperature } from "assets/images/temperature.svg";
import { ReactComponent as IconWater } from "assets/images/water.svg";
import { ReactComponent as IconWall } from "assets/images/wall.svg";
import { ReactComponent as IconWind } from "assets/images/wind.svg";
import { ReactComponent as IconIce } from "assets/images/ice.svg";
import { ReactComponent as IconMondpe } from "assets/images/mondpe.svg";
import { ReactComponent as IconWindow2 } from "assets/images/window2.svg";
import { ReactComponent as IconExtraInfo } from "assets/images/extra-info.svg";
import { ReactComponent as IconHash } from "assets/images/hash.svg";
import { ReactComponent as IconOldCalendar } from "assets/images/calendar-3.svg";
import { ReactComponent as IconCog } from "assets/images/cog.svg";
import { ReactComponent as IconHelp } from "assets/images/help-circle.svg";

import ResultatTabComponent from "components/dashboard/resultat-tab";
import API from "services/api";
import baseApi, { HEADER_KEY } from "services/api/base.api";
import QUERY_KEYS from "services/api/queryKeys";
import { getUrlParam } from "utils/iframeKey";

import "styles/dashboard.scss";

const ResultatTab = ResultatTabComponent as any;


function IframeMondpe() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  const [isTokenSet, setIsTokenSet] = useState(false);
  const [dataHomeUploaded, setDataHomeUploaded] = useState<any>(null);
  const [tab, setTab] = useState("1");

  useEffect(() => {
    baseApi.setDefaultHeader(HEADER_KEY.X_AUTHORIZTION, "pdf-renderer");

    // Try to get token from URL query param
    const tokenFromUrl = getUrlParam("token") || getUrlParam("key");
    if (tokenFromUrl) {
      baseApi.setToken(tokenFromUrl);
    }

    // Set X-Intent-Id header if provided in URL
    const intentIdFromUrl = searchParams.get("intent_id");
    if (intentIdFromUrl) {
      baseApi.setDefaultHeader(HEADER_KEY.X_INTENT_ID, intentIdFromUrl);
    }

    if (tokenFromUrl) {
      setIsTokenSet(true);
      return;
    }

    // Listen for token via postMessage from parent (for Authorization: Bearer header alternative)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "setToken" && event.data?.token) {
        baseApi.setToken(event.data.token);
        if (event.data.intentId) {
          baseApi.setDefaultHeader(HEADER_KEY.X_INTENT_ID, event.data.intentId);
        }
        setIsTokenSet(true);
      }
      // Also support direct token string or Bearer format (must look like a JWT)
      if (typeof event.data === "string") {
        const token = event.data.startsWith("Bearer ")
          ? event.data.slice(7)
          : event.data;
        // Only accept JWT-like tokens (three base64 parts separated by dots)
        if (token && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
          baseApi.setToken(token);
          setIsTokenSet(true);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // If no token from URL, still allow loading (might come via postMessage)
    const timeout = setTimeout(() => {
      setIsTokenSet(true);
    }, 100);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeout);
    };
  }, [searchParams]);

  // Disable cookieHub for iframe pages
  useEffect(() => {
    const previousBodyOverflow = document.body.style.getPropertyValue("overflow");
    const previousBodyOverflowPriority = document.body.style.getPropertyPriority("overflow");
    const previousBodyOverflowX = document.body.style.getPropertyValue("overflow-x");
    const previousBodyOverflowXPriority = document.body.style.getPropertyPriority("overflow-x");
    const previousBodyOverflowY = document.body.style.getPropertyValue("overflow-y");
    const previousBodyOverflowYPriority = document.body.style.getPropertyPriority("overflow-y");
    const previousHtmlOverflow = document.documentElement.style.getPropertyValue("overflow");
    const previousHtmlOverflowPriority = document.documentElement.style.getPropertyPriority("overflow");
    const previousHtmlOverflowX = document.documentElement.style.getPropertyValue("overflow-x");
    const previousHtmlOverflowXPriority = document.documentElement.style.getPropertyPriority("overflow-x");
    const previousHtmlOverflowY = document.documentElement.style.getPropertyValue("overflow-y");
    const previousHtmlOverflowYPriority = document.documentElement.style.getPropertyPriority("overflow-y");
    const cookieHubSelector = [
      "#cookiehub-widget",
      ".cookiehub-widget",
      "[id*=\"cookiehub\"]",
      "[class*=\"cookiehub\"]",
      "[data-region][class*=\"ch2-region\"]",
      ".ch2",
      ".ch2-container",
      ".ch2-dialog",
      ".ch2-icon",
      "style[data-ch2inline=\"true\"]",
    ].join(", ");

    const restoreProperty = (
      target: CSSStyleDeclaration,
      property: string,
      value: string,
      priority: string
    ) => {
      if (value) {
        target.setProperty(property, value, priority);
        return;
      }
      target.removeProperty(property);
    };

    const enforceIframeStyles = () => {
      document.body.style.setProperty("overflow", "auto", "important");
      document.body.style.setProperty("overflow-x", "auto", "important");
      document.body.style.setProperty("overflow-y", "auto", "important");
      document.documentElement.style.setProperty("overflow", "auto", "important");
      document.documentElement.style.setProperty("overflow-x", "auto", "important");
      document.documentElement.style.setProperty("overflow-y", "auto", "important");
    };

    const removeCookieHubElements = () => {
      document.querySelectorAll(cookieHubSelector).forEach((element) => {
        element.remove();
      });

      if ((window as any).cookiehub) {
        (window as any).cookiehub.hide?.();
        (window as any).cookiehub.close?.();
      }
    };

    enforceIframeStyles();

    // Hide cookieHub elements via CSS
    const style = document.createElement("style");
    style.innerHTML = `
      html,
      body {
        overflow: auto !important;
      }

      #cookiehub-widget,
      .cookiehub-widget,
      [id*="cookiehub"],
      [class*="cookiehub"],
      [data-region][class*="ch2-region"],
      .ch2,
      .ch2-container,
      .ch2-dialog,
      .ch2-icon,
      style[data-ch2inline="true"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    removeCookieHubElements();

    const observer = new MutationObserver(() => {
      enforceIframeStyles();
      removeCookieHubElements();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      observer.disconnect();
      restoreProperty(
        document.body.style,
        "overflow",
        previousBodyOverflow,
        previousBodyOverflowPriority
      );
      restoreProperty(
        document.body.style,
        "overflow-x",
        previousBodyOverflowX,
        previousBodyOverflowXPriority
      );
      restoreProperty(
        document.body.style,
        "overflow-y",
        previousBodyOverflowY,
        previousBodyOverflowYPriority
      );
      restoreProperty(
        document.documentElement.style,
        "overflow",
        previousHtmlOverflow,
        previousHtmlOverflowPriority
      );
      restoreProperty(
        document.documentElement.style,
        "overflow-x",
        previousHtmlOverflowX,
        previousHtmlOverflowXPriority
      );
      restoreProperty(
        document.documentElement.style,
        "overflow-y",
        previousHtmlOverflowY,
        previousHtmlOverflowYPriority
      );
      document.head.removeChild(style);
    };
  }, []);

  const {
    mutateAsync: mutateUploaded,
    isPending: isPendingUploaded,
    isError,
  } = useMutation({
    mutationFn: (params: any) => API.mock.getHomeUploadData(params),
  });

  const { data: dataDynamicInfo } = useQuery({
    queryKey: [QUERY_KEYS.GET_DYNAMIC_INFORMATION_RESULTAT, id],
    queryFn: () => API.mock.getDynamicInformationResultat(id as string),
    retry: 0,
    enabled: isTokenSet && !!id,
  });

  useEffect(() => {
    if (isTokenSet && id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenSet, id]);

  const loadData = async () => {
    try {
      // Call context endpoint first (required before home/home)
      await API.mock.getHomeContext(id as string, true);
      // Then load the main data
      const result = await mutateUploaded({ id });
      setDataHomeUploaded(result);
    } catch (e: any) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
        duration: 2,
      });
    }
  };

  const renderTabDuBien = () => {
    const TAB_DU_BIEN1: any[] = [
      {
        icon: IconRuler,
        label: `${_.get(dataHomeUploaded, "project_details.surface.0", "")} :`,
        value: _.get(dataHomeUploaded, "project_details.surface.1", ""),
      },
      {
        icon: IconHouse,
        label: `${_.get(dataHomeUploaded, "project_details.housing_type.0", "")} :`,
        value: _.get(dataHomeUploaded, "project_details.housing_type.1", ""),
      },
      {
        icon: IconCalendar,
        label: `${_.get(dataHomeUploaded, "project_details.construction_year.0", "")} `,
        value: _.get(dataHomeUploaded, "project_details.construction_year.1", ""),
      },
      {
        icon: IconStair,
        label: `${_.get(dataHomeUploaded, "project_details.floor.0", "")} : `,
        value: _.get(dataHomeUploaded, "project_details.floor.1", ""),
      },
    ];

    if (
      _.get(dataHomeUploaded, "project_details.recap.0") &&
      _.get(dataHomeUploaded, "project_details.recap.1") !== ""
    ) {
      TAB_DU_BIEN1.push({
        icon: IconExtraInfo,
        label: `${_.get(dataHomeUploaded, "project_details.recap.0", "")}`,
        value: _.get(dataHomeUploaded, "project_details.recap.1", ""),
      });
    }

    const TAB_DU_BIEN2: any[] = [
      {
        icon: IconWall,
        label: `${_.get(dataHomeUploaded, "project_details.materials.0", "")} : `,
        value: _.get(dataHomeUploaded, "project_details.materials.1", ""),
      },
    ];

    if (_.get(dataHomeUploaded, "project_details.window.0")) {
      TAB_DU_BIEN2.push({
        icon: IconWindow2,
        label: `${_.get(dataHomeUploaded, "project_details.window.0", "")} : `,
        value: _.get(dataHomeUploaded, "project_details.window.1", ""),
      });
    }

    TAB_DU_BIEN2.push({
      icon: IconWind,
      label: `${_.get(dataHomeUploaded, "project_details.airflow.0", "")} : `,
      value: _.get(dataHomeUploaded, "project_details.airflow.1", ""),
    });
    TAB_DU_BIEN2.push({
      icon: IconIce,
      label: `${_.get(dataHomeUploaded, "project_details.aircon.0", "")} : `,
      value: _.get(dataHomeUploaded, "project_details.aircon.1", ""),
    });
    TAB_DU_BIEN2.push({
      icon: IconEnergy,
      label: `${_.get(dataHomeUploaded, "project_details.energy.0", "")} : `,
      value: _.get(dataHomeUploaded, "project_details.energy.1", ""),
    });

    return (
      <div className="info-du-bien">
        <div className="info-du-bien__inner" style={{ display: "flex", flexWrap: "nowrap" }}>
          <ul style={{ flex: "1 1 50%", maxWidth: "50%" }}>
            {TAB_DU_BIEN1.map((item, index) => {
              const Icon = item.icon;
              return (
                <li key={index}>
                  <Flex align="center">
                    <Icon className="mr-1" />
                    {item.label && (
                      <span className="text-medium mr-1">{item.label}</span>
                    )}
                    {item.value && (
                      <p className="text-medium-bold text-truncate">
                        {item.value}
                      </p>
                    )}
                  </Flex>
                </li>
              );
            })}
          </ul>

          <ul style={{ flex: "1 1 50%", maxWidth: "50%" }}>
            {TAB_DU_BIEN2.map((item, index) => {
              const Icon = item.icon;
              return (
                <li key={index}>
                  <Flex align="start">
                    <div className="flex items-center flex-shrink-0">
                      <Icon className="mr-1 flex-shrink-0" />
                      {item.label && (
                        <p className="text-medium mr-1">{item.label} </p>
                      )}
                    </div>
                    {item.value && (
                      <p
                        className="text-medium-bold text-truncate max-w-[90%] relative top-[1px]"
                        style={{ lineHeight: "33px" }}
                      >
                        {item.value}
                      </p>
                    )}
                  </Flex>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="mt-3">
          <div className="flex items-center gap-1">
            <IconTemperature />
            <span className="text-medium">
              {_.get(dataHomeUploaded, "project_details.heating_system.0", "")}{" "}
              :{" "}
              <b>
                {_.get(dataHomeUploaded, "project_details.heating_system.1", "")}
              </b>
            </span>
          </div>
          <p className="text-medium mt-1">
            {_.get(
              dataHomeUploaded,
              "project_details.heating_system_details.1",
              ""
            )}
          </p>
        </div>

        <div className="mt-2">
          <div className="flex items-center gap-1">
            <IconWater />
            <span className="text-medium">
              {_.get(dataHomeUploaded, "project_details.hot_water.0", "")} :{" "}
              <b>
                {_.get(dataHomeUploaded, "project_details.hot_water.1", "")}
              </b>
            </span>
          </div>
          <p className="text-medium mt-1">
            {_.get(dataHomeUploaded, "project_details.hot_water_details.1", "")}
          </p>
        </div>
      </div>
    );
  };

  const renderTabDPE = () => {
    const doneOn = _.get(dataHomeUploaded, "dpe_details.done_on.1", dayjs());
    const validUntil = _.get(dataHomeUploaded, "dpe_details.valid_until.1", dayjs());

    return (
      <div className="manage-infos">
        <div className="flex items-center gap-1">
          <IconHash />
          <span className="text-[16px]">
            ADEME : <b>{_.get(dataHomeUploaded, "ademe", "")}</b>
          </span>
        </div>

        <div className="flex items-center gap-1">
          <IconOldCalendar />
          <span className="text-[16px]">
            {_.get(dataHomeUploaded, "dpe_details.done_on.0", "")} :{" "}
            <b>{dayjs.unix(doneOn).format("DD/MM/YYYY")}</b>
          </span>
        </div>

        <div className="flex items-center gap-1">
          <IconOldCalendar />
          <span className="text-[16px]">
            {_.get(dataHomeUploaded, "dpe_details.valid_until.0", "")} :{" "}
            <b>{dayjs.unix(validUntil).format("DD/MM/YYYY")}</b>
          </span>
        </div>

        <div className="flex items-center gap-1">
          <IconCog className="w-8 h-8" />
          <span className="text-[16px]">
            {_.get(dataHomeUploaded, "dpe_details.file_url.0", "")} :{" "}
            <b>{_.get(dataHomeUploaded, "dpe_details.file_url.1", "")}</b>
          </span>
        </div>

        <div className="flex items-center gap-1">
          <IconCog className="w-8 h-8" />
          <span className="text-[16px]">
            {_.get(dataHomeUploaded, "dpe_details.diagnotic_software.0", "")}{" "}
            :{" "}
            <b>
              {_.get(dataHomeUploaded, "dpe_details.diagnotic_software.1", "")}
            </b>
          </span>
        </div>

        {_.get(dataHomeUploaded, "dpe_details.dpe_building.1") && (
          <div className="flex items-center gap-1">
            <IconHouse className="w-8 h-8" />
            <span className="text-[16px]">
              {_.get(dataHomeUploaded, "dpe_details.dpe_building.0", "")}{" "}
              :{" "}
              <b>
                {_.get(dataHomeUploaded, "dpe_details.dpe_building.1", "")}
              </b>
            </span>
          </div>
        )}

        {_.get(dataHomeUploaded, "dpe_details.next_ref.1") && (
          <div className="flex items-center gap-1">
            <IconHash />
            <span className="text-[16px]">
              {_.get(dataHomeUploaded, "dpe_details.next_ref.0", "")} :{" "}
              <b>
                {_.get(dataHomeUploaded, "dpe_details.next_ref.1", "")}
              </b>
            </span>
          </div>
        )}

        {_.get(dataHomeUploaded, "dpe_details.prev_ref.1") && (
          <div className="flex items-center gap-1">
            <IconHash />
            <span className="text-[16px]">
              {_.get(dataHomeUploaded, "dpe_details.prev_ref.0", "")} :{" "}
              <b>
                {_.get(dataHomeUploaded, "dpe_details.prev_ref.1", "")}
              </b>
            </span>
          </div>
        )}

        {_.get(dataHomeUploaded, "dpe_details.prev_reason.1") && (
          <div className="flex items-center gap-1">
            <IconHelp className="w-8 h-8" />
            <span className="text-[16px]">
              {_.get(dataHomeUploaded, "dpe_details.prev_reason.0", "")} :{" "}
              <b>
                {_.get(dataHomeUploaded, "dpe_details.prev_reason.1", "")}
              </b>
            </span>
          </div>
        )}
      </div>
    );
  };

  if (!isTokenSet || isPendingUploaded || (!dataHomeUploaded && !isError)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin indicator={<LoadingOutlined />} size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Unable to load data</p>
      </div>
    );
  }

  return (
    <div className="energy-home energy-home--result iframe-mondpe p-4">
      {contextHolder}
      <Card className="energy-home__project-card">
        <div className="energy-home__project">
          <h3 className="flex items-center gap-x-2.5 font-bold text-[28px] lg:text-[40px] leading-[48px] mb-3">
            <IconMondpe className="w-7 h-7 flex-shrink-0" />
            {_.get(dataHomeUploaded, "project_name")}
          </h3>

          <Flex align="center" className="mb-2">
            <IconLocation />
            <span className="text-medium-bold ml-1">
              {_.get(dataHomeUploaded, "project_details.address.1", "")}
            </span>
          </Flex>

          <div className="energy-home__project-info" style={{ display: "flex", flexWrap: "nowrap", alignItems: "flex-start", gap: "16px", width: "100%" }}>
            <div style={{ flex: "1 1 60%", minWidth: 0 }}>
              <Tabs
                centered
                activeKey={tab}
                items={[
                  { key: "1", label: "Infos du bien", children: renderTabDuBien() },
                  { key: "2", label: "Infos du DPE", children: renderTabDPE() },
                ] as TabsProps["items"]}
                onChange={setTab}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ flex: "1 1 40%", minWidth: 0 }}>
              <ResultatTab dataDynamicInfo={dataDynamicInfo} dataHomeUploaded={dataHomeUploaded} />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default IframeMondpe;
